import type { Locale } from '@porotutu/i18n'
import { ActionLink } from '@porotutu/react/components/ActionLink/ActionLink'
import { DataTable } from '@porotutu/react/components/DataTable/DataTable'
import { EmptyState } from '@porotutu/react/components/EmptyState/EmptyState'
import { FilterBar } from '@porotutu/react/components/FilterBar/FilterBar'
import { Pagination } from '@porotutu/react/components/Pagination/Pagination'
import { Panel } from '@porotutu/react/components/Panel/Panel'
import { useSession, useT } from '@porotutu/react/session'
import { raisesInto } from '../../vocabulary'
import type { ManagedUserListQuery, ManagedUserPage } from '../queries'
import { columnsFor } from './columns'
import { UsersExport } from './Export'
import { filtersFor } from './filters'

export type UsersListProps = {
  page: ManagedUserPage
  query: ManagedUserListQuery
  locale: Locale
  onQuery: (patch: Record<string, string | number | undefined>) => void
  onReset: () => void
}

export function UsersList({ page, query, locale, onQuery, onReset }: UsersListProps) {
  const t = useT()
  const { permissions } = useSession()
  const raises = permissions.can(raisesInto(query.realm))
  const filtered = query.search !== undefined || query.role !== undefined

  return (
    <Panel
      level={1}
      title={t(`adm.users.tabs.${query.realm}`)}
      subtitle={t('adm.users.subtitle')}
      actions={
        <>
          <UsersExport query={query} locale={locale} />
          {raises ? (
            <ActionLink to="/users/$realm/new" params={{ realm: query.realm }}>
              {t('adm.users.index.new_button')}
            </ActionLink>
          ) : null}
        </>
      }
    >
      <FilterBar
        search={query.search ?? ''}
        searchPlaceholder={t('adm.users.filters.search_placeholder')}
        selects={filtersFor(t, query)}
        onChange={(patch) => onQuery({ ...patch, offset: undefined })}
        onReset={onReset}
      />

      <DataTable
        id="users-list"
        columns={columnsFor(t, locale, query)}
        rows={page.data}
        rowKey={(row) => row.id}
        meta={page.meta}
        empty={
          <EmptyState>{filtered ? t('adm.users.filtered_empty') : t('adm.users.empty')}</EmptyState>
        }
      />

      <Pagination meta={page.meta} />
    </Panel>
  )
}
